/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { CommerceList, Customer, ProductList, ProductSearchResult, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    ProductInput,
    getSimpleProducts,
    GetCustomerInput,
    getCustomer,
    getWishlistByCustomerId,
    buildWishlistLine,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { addLinesAsync, createCommerceListAsync, createGetByCustomerInput, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import * as React from 'react';
import { ProductComponent } from '../../../themes/dlvry-adventureworks/views/components/product.component';

import { IDlvrySearchResultContainerResources } from '../dlvry-search-result-container.props.autogenerated';
import { toast, toasts } from '../../../components/toast/toast';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../../shared-utils/validate-product-access';
import { IProductInventoryInformation } from '../../../components/common/product-inventory-utils';
import { onBuyHasAccessValidator } from '../../../shared-utils/attributes/on-buy-access-validator';
import { getCustomerAgreements } from '../../../shared-utils/agreement-product-utils';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { IBrandSchemeEntity } from '../../../actions/entities/brand-scheme-entity';
import { ISearchResultContainerState } from '../dlvry-search-result-container';
import { IProductRelationTypeForProduct } from '../../../actions/get-relation-types-by-ids';
import { OnChangeProductFunction } from '../../../components/dropdown/alternative-products';

interface IProductSearchResultItems {
    cart: ICartState | undefined;
    products: ProductSearchResultExt[];
    simpleProducts: SimpleProduct[];
    orgInformation?: Customer;
    customerInformation?: Customer;
    breakoffProducts: ISearchResultContainerState['breakoffProducts'];
    productQuantities: ISearchResultContainerState['productQuantities'];
    orderTemplates?: OrderTemplate[];
    unitConversions?: UnitOfMeasureConversion[];
    brandSchemes?: IBrandSchemeEntity[];
    wishlists: CommerceList[] | undefined;
    context: ICoreContext;
    config: { [key: string]: any };
    resources: IDlvrySearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    productInventoryStatus?: IProductInventoryInformation[];
    relationTypesByIds?: IProductRelationTypeForProduct[];
    disableBuyButtons?: boolean;
    showPrices?: boolean;
    validatedProductAccess?: ValidateProductAccessResult[];
    productInventoryInformation?: IProductInventoryInformation[];
    onUpdateQuantity: (product: ProductSearchResultExt, newQuantity: number) => void;
    onBreakoffChange: (product: ProductSearchResultExt, enabled: boolean) => void;
    onChangeProduct?: OnChangeProductFunction;
    onUpdateOrderTemplate: (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @param productInventoryStatus
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult, productInventoryStatus: IProductInventoryInformation[] | undefined): string | undefined {
    if (productInventoryStatus) {
        const shippingInventory = productInventoryStatus.find(shipping => shipping.ProductAvailableQuantity.ProductId === product.RecordId);
        if (shippingInventory && shippingInventory.StockLevelLabel) {
            return shippingInventory.StockLevelLabel;
        }
    }
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

async function callAddWishlistLine(context: ICoreContext, productId: number, wishlistId: number, toastErrorHeader?: string): Promise<void> {
    try {
        const customer = await getCustomerAccount(context);
        if (customer && customer.AccountNumber) {
            const createdCommerceList = await addLinesAsync({ callerContext: context.actionContext },
                wishlistId,
                [
                    buildWishlistLine(productId, customer.AccountNumber, wishlistId)
                ]);
            context.actionContext.update(createGetByCustomerInput({}, null), [createdCommerceList]);
        }
    } catch (err) {
        const error = err as unknown as any;
        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
        toasts.error(toastErrorHeader, error, { footer: footer });
    }
}

async function getCustomerAccount(context: ICoreContext) {
    const customerInput = new GetCustomerInput(context.actionContext.requestContext.apiSettings);
    return getCustomer(customerInput, context.actionContext);
}

function getWishlistLineIdFromProductId(wishlists: CommerceList[] | undefined, productId: number | undefined): number | null {
    if (!wishlists ||
        !productId ||
        wishlists.length === 0 ||
        !wishlists[0].CommerceListLines ||
        wishlists[0].CommerceListLines.length === 0) {
        return null;
    }

    const foundWishlistItem = wishlists[0].CommerceListLines.filter(commerceListLine => {
        return commerceListLine.ProductId === productId;
    });

    if (foundWishlistItem && foundWishlistItem[0].LineId) {
        return foundWishlistItem[0].LineId;
    }
    return null;
}

async function moveItemToWishlist(context: ICoreContext, resources: IDlvrySearchResultContainerResources, product: ProductSearchResult, defaultWishlistName: string): Promise<void> {
    if (!context || !product || !context.request.user.catalogId) {
        return Promise.resolve();
    }

    const productInputs: ProductInput[] = [];
    const productInput = new ProductInput(
        product.RecordId,
        context.request.apiSettings,
        undefined, undefined,
        context.request,
        +context.request.user.catalogId
    );
    productInputs.push(productInput);

    const simpleProduct = await getSimpleProducts(productInputs, context.actionContext);

    if (context.request.user.isAuthenticated && simpleProduct && simpleProduct.length >= 0) {
        const customer = await getCustomerAccount(context);
        const wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
        if (!wishlists || wishlists.length === 0) {
            const wishlistName = defaultWishlistName;
            if (customer && customer.AccountNumber) {
                const listData: CommerceList = {
                    CustomerId: customer.AccountNumber,
                    Name: wishlistName,
                    CommerceListTypeValue: 1,
                    Id: 0
                };
                try {
                    const commerceList = await createCommerceListAsync({
                            callerContext: context.actionContext
                        },
                        listData);
                    await callAddWishlistLine(context, simpleProduct[0].RecordId, commerceList.Id, resources.toastErrorHeader);
                } catch (err) {
                    const error = err as unknown as any;
                    const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                    toasts.error(resources.toastErrorHeader, error, { footer });
                }
            }
        } else {
            await callAddWishlistLine(context, simpleProduct[0].RecordId, wishlists[0].Id, resources.toastErrorHeader);

            toast({
                text: resources.addedProductToWishlistText.replace('{productName}', simpleProduct[0].Name || ''),
                type: 'success',
                autoClose: 3000,
                position: 'bottom-right',
                canClose: true,
                showProgress: false
            });
        }
    }
}

function removeItemFromWishlist(context: ICoreContext, wishlists: CommerceList[] | undefined, product: ProductSearchResult, toastErrorHeader?: string): void {
    const wishlistLineId = getWishlistLineIdFromProductId(wishlists, product.RecordId);
    if (wishlists && wishlists[0] && wishlistLineId) {
        removeLinesAsync({ callerContext: context.actionContext }, wishlists[0].Id, [{ LineId: wishlistLineId }])
            .then(commerceList => {
                const newWishlists = [
                    ...wishlists
                ];
                newWishlists[0] = commerceList;
                context.actionContext.update(createGetByCustomerInput({}, null), newWishlists);
            })
            .catch(error => {
                const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                toasts.error(toastErrorHeader, error, { footer });
            });
    }
}

function getWishlistButton(context: ICoreContext, resources: IDlvrySearchResultContainerResources, product: ProductSearchResult, wishlists: CommerceList[] | undefined, defaultWishlistName: string) {
    const isWishlisted = wishlists && wishlists.length > 0 && wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId) ? true : false;
    let className = 'btn-add-to-wishlist';

    if (isWishlisted) {
        className += ' is-wishlisted';
    }

    return (
        <div>
            <button
                className={className}
                title={!isWishlisted ? resources.addToWishlistButtonText : resources.removeFromWishlistButtonText}
                aria-label={!isWishlisted ? resources.addToWishlistButtonText : resources.removeFromWishlistButtonText}
                onClick={async () => {
                    if (!isWishlisted) {
                        await moveItemToWishlist(context, resources, product, defaultWishlistName);
                    } else {
                        removeItemFromWishlist(context, wishlists, product, resources.toastErrorHeader);
                    }
                }
            }>
                {!isWishlisted ? resources.addToWishlistButtonText : resources.removeFromWishlistButtonText}
            </button>
        </div>
    );
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    cart,
    products,
    simpleProducts,
    orgInformation,
    customerInformation,
    breakoffProducts,
    productQuantities,
    orderTemplates,
    unitConversions,
    brandSchemes,
    wishlists,
    context,
    config,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    productInventoryStatus,
    relationTypesByIds,
    validatedProductAccess,
    disableBuyButtons,
    showPrices,
    productInventoryInformation,
    onUpdateQuantity,
    onBreakoffChange,
    onChangeProduct,
    onUpdateOrderTemplate,
    retrieveOrderTemplates
}) => {
    const cartQueue = React.useMemo(() => new PQueue({ concurrency: 1 }), []);

    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };
    return (
        <ul className='ms-product-search-result__grid 2'>
            {products.map((product: ProductSearchResultExt, index: number) => (
                <li className='ms-product-search-result__item' key={index}>
                    <ProductComponent
                        context={context}
                        telemetryContent={telemetryContent}
                        imageSettings={imageSettings}
                        freePriceText={resources.priceFree}
                        originalPriceText={resources.originalPriceText}
                        currentPriceText={resources.currentPriceText}
                        ratingAriaLabel={resources.ratingAriaLabel}
                        depositSuffixText={resources.depositSuffixText}
                        priceNotLoggedInText={resources.priceNotLoggedInText}
                        allowBack={allowBackNavigation}
                        id={moduleId}
                        key={product.RecordId}
                        typeName={moduleType}
                        data={{
                            cart,
                            product,
                            simpleProduct: simpleProducts.find(x => x.RecordId === product.RecordId),
                            customerInformation,
                            customerAgreements: getCustomerAgreements(context, orgInformation),
                            unitConversions: unitConversions?.filter(x => x.ProductRecordId === product.RecordId),
                            brandSchemes,
                            relationTypesByIds
                        }}
                        currentQuantity={productQuantities[product.RecordId] || 0}
                        breakoff={breakoffProducts[product.RecordId] || false}
                        onUpdateQuantity={onUpdateQuantity}
                        onBreakoffChange={onBreakoffChange}
                        onChangeProduct={onChangeProduct}
                        cartQueue={cartQueue}
                        retrieveOrderTemplates={retrieveOrderTemplates}
                        onUpdateOrderTemplates={onUpdateOrderTemplate}
                        quickViewButton={quickviewSlot}
                        productComparisonButton={productComparisonButton}
                        wishlistButton={getWishlistButton(context, resources, product, wishlists, resources.defaultWishlistName)}
                        inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product,productInventoryStatus)}
                        inventoryStatus={productInventoryInformation?.find(x => x.ProductAvailableQuantity.ProductId === product.RecordId)}
                        isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                        disableBuyButton={disableBuyButtons}
                        showPrice={showPrices}
                        alternativeProductsMode='edit'
                        userHasAccess={validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess}
                        priceResources={priceResources}
                        swatchItemAriaLabel={resources.swatchItemAriaLabel}
                        addToCartText={resources.addToCartButtonText}
                        updateCartText={resources.updateCartText}
                        removeFromCartText={resources.removeFromCartText}
                        outOfStockText={resources.outOfStockText}
                        expiredText={resources.expiredText}
                        outOfSeasonBuyButtonText={resources.outOfSeasonBuyButtonText}
                        priceErrorText={resources.priceErrorText}
                        toastStrings={{
                            errorHeader: resources.toastErrorHeader,
                            productsAddedToCart: resources.productsAddedToCart,
                            productsRemovedFromCart: resources.productsRemovedFromCart,
                            productsUpdatedInCart: resources.productsUpdatedInCart,
                            goToCart: resources.toastGoToCart,
                            noAccessToProduct: resources.toastNoAccessToProduct
                        }}
                        inCartTooltipText={resources.inCartTooltipText}
                        transitTooltip={resources.transitTooltip}
                        agreementProductTooltip={resources.agreementProductTooltip}
                        alternativeProductsButtonText={resources.alternativeProductsButtonText}
                        alternativeProductsChangeProductButtonText={resources.alternativeProductsBuyButtonText}
                        alternativeProductsLoadingText={resources.alternativeProductsLoadingText}
                        alternativeProductsNoResultsText={resources.alternativeProductsNoResultsText}
                        alternativeProductsErrorText={resources.alternativeProductsErrorText}
                        alternativeProductsCloseButtonText={resources.modalCloseButtonText}
                        dimensionAvailabilities={productsDimensionAvailabilities?.find(
                            dimensionAvailability =>
                                ArrayExtensions.hasElements(dimensionAvailability) &&
                                dimensionAvailability[0].masterProductId === product.MasterProductId
                        )}
                        addToOrderTemplateResources={{
                            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
                            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
                            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
                            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
                            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
                            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
                            selectTemplatesText: resources.selectTemplatesText,
                            addToTemplateButtonText: resources.addToTemplateButtonText,
                            lineItemsText: resources.lineItemsText,
                            createOrderTemplateHeader: resources.createOrderTemplateHeader,
                            orderTemplateTitle: resources.orderTemplateTitle,
                            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
                            createOrderTemplateDescription: resources.createOrderTemplateDescription,
                            defaultOrderTemplateName: resources.defaultOrderTemplateName,
                            createOrderTemplateButtonText: resources.createOrderTemplateButtonText,
                            cancelNewOrderTemplateCreationButtonText: resources.cancelNewOrderTemplateCreationButtonText,
                            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
                            continueShoppingButtonText: resources.continueShoppingButtonText,
                            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
                            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
                            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
                            priceFree: resources.priceFree,
                            originalPriceText: resources.originalPriceText,
                            currentPriceText: resources.currentPriceText,
                            duplicatedProductsHeader: resources.duplicatedProductsHeader,
                            duplicatedProductsDescription: resources.duplicatedProductsDescription,
                            updateQuantityButtonText: resources.updateQuantityButtonText,
                            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText,
                            addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                            addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip
                        }}
                        onBuyValidate={(product) => onBuyHasAccessValidator(
                            context,
                            orgInformation,
                            product,
                            resources.toastNoAccessToProduct
                        )}
                    />
                </li>
            ))}
        </ul>
    );
};
